import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { Search, SlidersHorizontal, X, Loader2, MapPin, Grid3X3, List, Bell, Compass, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import ListingCard from '../components/ListingCard';
import { devLog, devError, devWarn } from '../utils/logger';

const conditions = [
  { id: 'excellent', name: 'Excellent' },
  { id: 'good', name: 'Good' },
  { id: 'fair', name: 'Fair' },
  { id: 'salvage', name: 'Salvage' }
];

const availabilities = [
  { id: 'available', name: 'Available' },
  { id: 'sold', name: 'Sold' },
  { id: 'reserved', name: 'Reserved' }
];

export default function MarketplacePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [geoCoords, setGeoCoords] = useState(null); // {lat, lng} or null
  const [geoLocating, setGeoLocating] = useState(false);
  const [radiusKm, setRadiusKm] = useState(50);

  // Filters state
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: searchParams.get('category') || '',
    condition: searchParams.get('condition') || '',
    availability: searchParams.get('availability') || 'available',
    location: searchParams.get('location') || '',
    has_digital_model: searchParams.get('has_digital_model') === 'true',
    passport_quality: searchParams.get('passport_quality') === 'true',
    supplier: searchParams.get('supplier') || '',
    patinamart_only: searchParams.get('patinamart_only') === 'true',
  });

  const requestGeolocation = useCallback(() => {
    if (!('geolocation' in navigator)) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    setGeoLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoLocating(false);
        toast.success('Showing listings near you');
      },
      () => {
        setGeoLocating(false);
        toast.error('Could not get your location — please grant permission');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 },
    );
  }, []);

  const clearGeo = () => {
    setGeoCoords(null);
    toast.info('Showing all listings');
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/listings/categories`);
        setCategories(res.data.categories);
      } catch (error) {
        devError('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.append('search', filters.search);
        if (filters.category) params.append('category', filters.category);
        if (filters.condition) params.append('condition', filters.condition);
        if (filters.availability) params.append('availability', filters.availability);
        if (filters.location) params.append('location', filters.location);
        if (filters.has_digital_model) params.append('has_digital_model', 'true');
        if (filters.passport_quality) params.append('passport_quality', 'true');
        if (geoCoords) {
          params.append('lat', String(geoCoords.lat));
          params.append('lng', String(geoCoords.lng));
          params.append('radius_km', String(radiusKm));
        }
        params.append('limit', '200');

        const res = await axios.get(`${API}/listings?${params.toString()}`);
        setListings(res.data);
      } catch (error) {
        devError('Error fetching listings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, [filters, geoCoords, radiusKm]);

  const updateFilter = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL params
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v && v !== 'available') params.append(k, v);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      condition: '',
      availability: 'available',
      location: '',
      has_digital_model: false,
      passport_quality: false,
      supplier: ''
    });
    setSearchParams({});
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => 
    v && k !== 'availability' && !(k === 'availability' && v === 'available')
  ).length;

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="marketplace-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Marketplace</p>
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-foreground mb-4">
              Browse Materials
            </h1>
            <p className="text-muted-foreground max-w-2xl">
              Discover exceptional salvaged building materials from verified suppliers worldwide.
            </p>
          </div>
          {user && (
            <Button
              onClick={() => navigate('/alerts')}
              variant="outline"
              className="border-accent/40 text-accent hover:bg-accent/10"
              data-testid="create-alert-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Set Up Alert
            </Button>
          )}
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Search materials, types, locations..."
              className="w-full h-12 pl-12 pr-4 bg-secondary/30 border border-border/40 rounded-lg text-foreground placeholder:text-muted-foreground/50"
              data-testid="search-input"
            />
          </div>

          {/* Category Filter */}
          <Select value={filters.category} onValueChange={(v) => updateFilter('category', v)}>
            <SelectTrigger className="w-full md:w-48 h-12 bg-secondary/30 border-border/40" data-testid="category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* More Filters Button */}
          <Button
            variant="outline"
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={`h-12 border-border/40 ${activeFilterCount > 0 ? 'border-accent/50 text-accent' : ''}`}
            data-testid="filters-toggle"
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 w-5 h-5 rounded-full bg-accent text-accent-foreground text-xs flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>

          {/* Near-me geolocation */}
          {geoCoords ? (
            <div className="h-12 px-3 flex items-center gap-2 rounded-md border border-accent/40 bg-accent/10 text-accent" data-testid="geo-active">
              <Compass className="w-4 h-4" />
              <span className="text-sm font-medium whitespace-nowrap">
                Within {radiusKm} km
              </span>
              <button onClick={clearGeo} aria-label="Clear location" className="ml-1 hover:text-foreground" data-testid="geo-clear-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={requestGeolocation}
              disabled={geoLocating}
              className="h-12 border-border/40"
              data-testid="geo-locate-btn"
            >
              {geoLocating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Compass className="w-4 h-4 mr-2" />
              )}
              Near me
            </Button>
          )}

          {/* View Toggle */}
          <div className="hidden md:flex items-center gap-1 bg-secondary/30 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}
              data-testid="grid-view-btn"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-card text-foreground' : 'text-muted-foreground'}`}
              data-testid="list-view-btn"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Source toggle — hide external (Gumtree / FB / Craigslist) listings.
            Persisted via URL so sharing a "verified only" search is easy. */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <button
            type="button"
            onClick={() => updateFilter('patinamart_only', !filters.patinamart_only)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filters.patinamart_only
                ? 'bg-accent/15 border-accent/40 text-accent'
                : 'bg-secondary/30 border-border/30 text-muted-foreground hover:text-foreground'
            }`}
            data-testid="patinamart-only-toggle"
            title="Excludes listings imported from Gumtree / Facebook Marketplace / Craigslist"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            {filters.patinamart_only ? 'Showing PatinaMart verified only' : 'Only PatinaMart verified'}
          </button>
          {filters.patinamart_only && (
            <span className="text-xs text-muted-foreground">
              Hiding Gumtree / Facebook Marketplace / Craigslist imports
            </span>
          )}
        </div>

        {/* Extended Filters Panel */}
        {filtersOpen && (
          <div className="bg-card/50 border border-border/20 rounded-lg p-6 mb-8 animate-slideUp" data-testid="filters-panel">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Condition */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Condition</label>
                <Select value={filters.condition} onValueChange={(v) => updateFilter('condition', v)}>
                  <SelectTrigger className="w-full bg-secondary/30 border-border/40">
                    <SelectValue placeholder="Any condition" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Condition</SelectItem>
                    {conditions.map((cond) => (
                      <SelectItem key={cond.id} value={cond.id}>{cond.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Availability */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Availability</label>
                <Select value={filters.availability} onValueChange={(v) => updateFilter('availability', v)}>
                  <SelectTrigger className="w-full bg-secondary/30 border-border/40">
                    <SelectValue placeholder="Available" />
                  </SelectTrigger>
                  <SelectContent>
                    {availabilities.map((avail) => (
                      <SelectItem key={avail.id} value={avail.id}>{avail.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Location</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => updateFilter('location', e.target.value)}
                    placeholder="City or region"
                    className="w-full h-10 pl-10 pr-4 bg-secondary/30 border border-border/40 rounded-md text-foreground placeholder:text-muted-foreground/50"
                    data-testid="location-filter"
                  />
                </div>
              </div>

              {/* Radius (only when geolocated) */}
              {geoCoords && (
                <div data-testid="geo-radius-control">
                  <label className="text-sm text-muted-foreground mb-2 flex items-center justify-between">
                    <span>Distance</span>
                    <span className="text-accent font-medium">{radiusKm} km</span>
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="500"
                    step="5"
                    value={radiusKm}
                    onChange={(e) => setRadiusKm(Number(e.target.value))}
                    className="w-full accent-accent"
                    data-testid="geo-radius-slider"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground/60 mt-1">
                    <span>5</span><span>250</span><span>500 km</span>
                  </div>
                </div>
              )}

              {/* Digital Model */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">BIM Ready</label>
                <button
                  onClick={() => updateFilter('has_digital_model', !filters.has_digital_model)}
                  className={`w-full h-10 px-4 rounded-md border transition-colors ${
                    filters.has_digital_model 
                      ? 'bg-primary/20 border-primary/50 text-primary' 
                      : 'bg-secondary/30 border-border/40 text-muted-foreground'
                  }`}
                  data-testid="bim-filter"
                >
                  Has 3D Model
                </button>
              </div>

              {/* Passport quality */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Passport quality</label>
                <button
                  onClick={() => updateFilter('passport_quality', !filters.passport_quality)}
                  className={`w-full h-10 px-4 rounded-md border transition-colors ${
                    filters.passport_quality
                      ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                      : 'bg-secondary/30 border-border/40 text-muted-foreground'
                  }`}
                  data-testid="passport-quality-filter"
                  title="Carbon stats + salvage origin documented — accepted as Green Star Fitouts / Climate Active / NABERS evidence"
                >
                  GSF · Climate Active evidence
                </button>
              </div>
            </div>

            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="mt-4 text-sm text-muted-foreground hover:text-accent transition-colors flex items-center gap-1"
                data-testid="clear-filters"
              >
                <X className="w-4 h-4" /> Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            {loading ? 'Loading...' : `${listings.length} materials found`}
          </p>
        </div>

        {/* Listings Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : listings.length > 0 ? (
          <div 
            className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6' 
              : 'flex flex-col gap-4'
            }
            data-testid="listings-grid"
          >
            {listings.map((listing) => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24" data-testid="no-results">
            <p className="text-muted-foreground mb-4">No materials found matching your criteria.</p>
            <Button onClick={clearFilters} variant="outline" className="border-accent/40 text-accent">
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
